import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { usePreviewSearch, widget, WidgetDataType } from '@sitecore-search/react';
import { useSearchContext } from '../sitecore-search-block/components/search-provider/search-context';
import GoogleIcon from '../../components/google-icon';
import Loader from '../../components/loader';

const DebouncedSearchInputField = ({ onChange, placeholder = 'Enter search keyword', value, onFocus, onBlur, onKeyDown }) => {
    const [localValue, setLocalValue] = useState(value);

    // Update local value when prop value changes (for initial load or external updates)
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Debounce the onChange callback
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localValue !== value) {
                onChange({ target: { value: localValue } });
            }
        }, 100); // 300ms delay

        return () => clearTimeout(timer);
    }, [localValue, onChange, value]);

    const handleInputChange = e => {
        setLocalValue(e.target.value);
    };

    return (
        <div className="search-input-field">
            <GoogleIcon icon={'search'} />
            <input
                type="text"
                name="search"
                value={localValue}
                onChange={handleInputChange}
                placeholder={placeholder}
                autoComplete="off"
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
            />
        </div>
    );
};

DebouncedSearchInputField.propTypes = {
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func
};

const SearchPageHeader = ({ sectionTitle, sectionSubtitle, onSubmit, initialSearchValue = '', onClearSearch }) => {
    // Initialize search keyword from URL params or initialSearchValue
    const getInitialSearchValue = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('q') || initialSearchValue;
        }
        return initialSearchValue;
    };

    const { dataSource, setAutoCorrectSuggestion } = useSearchContext();

    const [searchKeyword, setSearchKeyword] = useState(getInitialSearchValue());
    const [showSuggestions, setShowSuggestions] = useState(false);

    const {
        widgetRef,
        actions: { onKeyphraseChange, onSuggestionClick },
        queryResult: {
            isFetching,
            isLoading,
            data: {
                suggestion: {
                    title_context_aware: articleSuggestions = [],
                    did_you_mean_title: autoCorrectSuggestions = []
                } = {}
            } = {}
        }
    } = usePreviewSearch({
        query: query => {
            query.getRequest().setSources([dataSource]);
        },
        state: {
            suggestionsList: [
                { suggestion: 'title_context_aware', max: 4 },
                { suggestion: 'did_you_mean_title', max: 4 }
            ],
            itemsPerPage: 10,
            keyphrase: searchKeyword
        }
    });

    useEffect(() => {
        setAutoCorrectSuggestion(autoCorrectSuggestions.length > 0 ? autoCorrectSuggestions[0]?.text : '');
    }, [autoCorrectSuggestions, setAutoCorrectSuggestion]);

    const handleFormSubmit = e => {
        e.preventDefault();

        // Update URL query parameters
        const url = new URL(window.location);
        if (searchKeyword.trim()) {
            url.searchParams.set('q', searchKeyword.trim());
        } else {
            url.searchParams.delete('q');
        }
        window.history.pushState({}, '', url);

        if (onSubmit) {
            onSubmit(searchKeyword);
        }
    };

    const handleInputChange = e => {
        setSearchKeyword(e.target.value);
        onKeyphraseChange({ keyphrase: e.target.value });
        setShowSuggestions(e.target.value.trim() !== '');
    };

    const handleClearSearch = () => {
        setSearchKeyword('');
        onKeyphraseChange({ keyphrase: '' });

        if (onClearSearch) {
            onClearSearch();
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter') {
            handleFormSubmit(e);
            e.target.blur();
        }
    };

    const hasAutoCorrectItems = autoCorrectSuggestions.length > 0;
    const selectedWordCorrection = autoCorrectSuggestions[0]?.text || '';

    return (
        <section className="s-search-page-header">
            <div className="page-header-container">
                <div className="page-header-content-box">
                    <div className="page-header-content">
                        <h1 className="page-header-title">{sectionTitle}</h1>
                        {sectionSubtitle && <p className="page-header-subtitle">{sectionSubtitle}</p>}
                    </div>

                    <div className="search-form-block">
                        <form onSubmit={handleFormSubmit}>
                            <div className="search-form-container">
                                <div className="search-input-wrapper">
                                    <DebouncedSearchInputField
                                        onChange={handleInputChange}
                                        value={searchKeyword}
                                        placeholder="Enter search keyword"
                                        onFocus={() => {
                                            if (searchKeyword.trim() !== '') {
                                                setShowSuggestions(true);
                                            }
                                        }}
                                        onBlur={() => {
                                            // Delay hiding suggestions to allow click event to register
                                            setTimeout(() => {
                                                setShowSuggestions(false);
                                            }, 200);
                                        }}
                                        onKeyDown={handleKeyDown}
                                    />
                                    {searchKeyword !== '' && (
                                        <button
                                            type="button"
                                            className="btn-clear-search"
                                            onClick={handleClearSearch}
                                        >
                                            <GoogleIcon icon="close" />
                                        </button>
                                    )}
                                </div>
                                <div ref={widgetRef} className={`search-suggestion-wrapper${showSuggestions ? ' is-show' : ''}`}>
                                    {(isFetching || isLoading) && (
                                        <Loader />
                                    )}

                                    {!isFetching && !isLoading && articleSuggestions.length > 0 && (
                                        <>
                                            <div className="search-suggestion-heading">
                                                Suggested searches
                                            </div>
                                            <div className="suggestion-list">
                                                {articleSuggestions.map((suggestion, index) => (
                                                    <button
                                                        type="button"
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={`suggestion-${index}`}
                                                        className="suggestion-item"
                                                        onClick={() => {
                                                            onSuggestionClick({
                                                                name: 'title_context_aware',
                                                                title: 'Suggested search',
                                                                value: suggestion.text,
                                                                displayName: suggestion.text
                                                            });
                                                            setSearchKeyword(suggestion.text);
                                                            onKeyphraseChange({ keyphrase: suggestion.text });
                                                            if (onSubmit) {
                                                                onSubmit(suggestion.text);
                                                            }
                                                        }}
                                                    >
                                                        {suggestion.text}
                                                    </button>
                                                )
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="default-suggestion-item">
                                        {(!isFetching && !isLoading && hasAutoCorrectItems) && (
                                            <button
                                                type="submit"
                                                className="search-current-keyword-btn"
                                                onClick={() => {
                                                    setSearchKeyword(selectedWordCorrection);
                                                    onKeyphraseChange({ keyphrase: selectedWordCorrection });
                                                }}
                                            >
                                                <span className="btn-label">Did you mean &quot;{selectedWordCorrection}&quot;</span>
                                                <GoogleIcon icon="arrow_forward" />
                                            </button>
                                        )}

                                        {!hasAutoCorrectItems && (
                                            <button
                                                type="submit"
                                                className="search-current-keyword-btn"
                                                onClick={() => {
                                                    setSearchKeyword(searchKeyword);
                                                    onKeyphraseChange({ keyphrase: searchKeyword });
                                                }}
                                            >
                                                <span className="btn-label">Search for &quot;{searchKeyword}&quot;</span>
                                                <GoogleIcon icon="arrow_forward" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="device-pattern">
                <img src="/assets/images/device-pattern.png" alt="pattern page header standard"/>
            </div>
        </section>
    );
};

SearchPageHeader.propTypes = {
    sectionTitle: PropTypes.string.isRequired,
    sectionSubtitle: PropTypes.string,
    onSubmit: PropTypes.func,
    initialSearchValue: PropTypes.string,
    onClearSearch: PropTypes.func
};

const SearchPageHeaderWidget = widget(SearchPageHeader, WidgetDataType.PREVIEW_SEARCH, 'content');

export default SearchPageHeaderWidget;
